import { useState } from 'react';
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonPage,
} from '@ionic/react';
import { eyeOffOutline, eyeOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { GoogleSignInButton } from '../../components/GoogleSignInButton.js';
import { toUserMessage } from '../../lib/errors.js';

// "Ricordami": credenziali salvate in locale (localStorage del WebView, storage
// privato dell'app). Gate esplicito sul flag: senza spunta non si salva nulla e
// togliendo la spunta si cancella subito quanto salvato.
const REMEMBER_KEY = 'trackeat:rememberMe';
const SAVED_EMAIL_KEY = 'trackeat:savedEmail';
const SAVED_PW_KEY = 'trackeat:savedPassword';

function readLS(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeLS(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* storage non disponibile */ }
}
function removeLS(key: string): void {
  try { localStorage.removeItem(key); } catch { /* storage non disponibile */ }
}
function forgetSavedCredentials(): void {
  removeLS(REMEMBER_KEY);
  removeLS(SAVED_EMAIL_KEY);
  removeLS(SAVED_PW_KEY);
}

export function SignInPage(): JSX.Element {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const [remember, setRemember] = useState(() => readLS(REMEMBER_KEY) === '1');
  const [email, setEmail] = useState(() => {
    if (readLS(REMEMBER_KEY) === '1') {
      const saved = readLS(SAVED_EMAIL_KEY);
      if (saved) return saved;
    }
    try { return localStorage.getItem('trackeat:lastSignupEmail') ?? ''; } catch { return ''; }
  });
  const [password, setPassword] = useState(() =>
    readLS(REMEMBER_KEY) === '1' ? (readLS(SAVED_PW_KEY) ?? '') : '',
  );
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function onRememberChange(checked: boolean): void {
    setRemember(checked);
    // Togliere la spunta = dimentica subito le credenziali salvate.
    if (!checked) forgetSavedCredentials();
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      // Salva solo dopo un accesso riuscito, e solo col flag attivo.
      if (remember) {
        writeLS(REMEMBER_KEY, '1');
        writeLS(SAVED_EMAIL_KEY, email.trim());
        writeLS(SAVED_PW_KEY, password);
      } else {
        forgetSavedCredentials();
      }
    } catch (err: unknown) {
      setError(toUserMessage(err, t('auth.invalidCredentials')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <IonPage>
      <IonContent className="te-page">
        <div className="te-auth-shell" style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '32px 24px',
        }}>
          {/* Brand hero */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 20,
              background: 'linear-gradient(135deg, #0066cc, #8b5cf6)',
              color: '#fff', fontSize: 32, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(0,102,204,.25)',
            }}>
              TE
            </div>
            <h1 style={{ margin: '16px 0 4px', fontSize: 26, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
              TrackEAT
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              Gestione HACCP per la ristorazione
            </p>
          </div>

          <form onSubmit={onSubmit}>
            <div style={{
              background: '#fff', borderRadius: 16, padding: 16,
              boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.04)',
            }}>
              <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '4px', '--inner-padding-end': '4px', marginBottom: 4 } as React.CSSProperties}>
                <IonInput
                  type="email"
                  label={t('auth.email')}
                  labelPlacement="stacked"
                  value={email}
                  required
                  autocomplete="email"
                  onIonInput={(ev) => setEmail(String(ev.detail.value ?? ''))}
                />
              </IonItem>
              <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '4px', '--inner-padding-end': '4px' } as React.CSSProperties}>
                <IonInput
                  type={showPassword ? 'text' : 'password'}
                  label={t('auth.password')}
                  labelPlacement="stacked"
                  value={password}
                  required
                  autocomplete="current-password"
                  autocapitalize="off"
                  spellcheck={false}
                  // Ionic imposta clearOnEdit=true di default per type="password":
                  // alla prima modifica dopo il focus azzera TUTTO il valore (ed è
                  // implicato anche nel bug del toggle text/password, Ionic #28122).
                  // Con false: cancellando un carattere resta il resto del testo.
                  clearOnEdit={false}
                  onIonInput={(ev) => setPassword(String(ev.detail.value ?? ''))}
                >
                  {/* Toggle mostra/nascondi gestito da stato React: una volta
                      mostrata la password resta visibile in modo deterministico,
                      immune ai reset interni del toggle nativo Ionic. */}
                  <IonButton
                    slot="end"
                    type="button"
                    fill="clear"
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    <IonIcon slot="icon-only" icon={showPassword ? eyeOffOutline : eyeOutline} aria-hidden="true" />
                  </IonButton>
                </IonInput>
              </IonItem>
            </div>

            <div style={{ marginTop: 14, paddingLeft: 4 }}>
              <IonCheckbox
                checked={remember}
                onIonChange={(ev) => onRememberChange(ev.detail.checked)}
                labelPlacement="end"
                style={{ '--size': '20px', fontSize: 14, color: '#334155' } as React.CSSProperties}
              >
                {t('auth.rememberMe')}
              </IonCheckbox>
            </div>

            {error && (
              <div style={{
                background: '#fee2e2', color: '#b91c1c',
                padding: '10px 14px', borderRadius: 12, fontSize: 13,
                marginTop: 12,
              }}>
                {error}
              </div>
            )}

            <IonButton
              type="submit"
              expand="block"
              disabled={loading}
              style={{ '--border-radius': '12px', marginTop: 20, height: 48 } as React.CSSProperties}
            >
              {loading ? t('common.loading') : t('auth.signin')}
            </IonButton>
          </form>

          <GoogleSignInButton />
        </div>
      </IonContent>
    </IonPage>
  );
}
